/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { getListingVariants } from './getListingVariants';
import { findListingEntity } from './helpers/entityReadHelpers';
import { upsertListing } from './helpers/entityAdapter';
import { showPublishedToChannelToast } from './helpers/showPublishedToChannelToast';
import handlePublishListingFailures from './helpers/handlePublishListingFailures';
import handlePublishVariantMediaWarnings from './helpers/handlePublishVariantMediaWarnings';
import { showPublishFailedDialog } from '../../components/ProductInfo/PublishFailedDialog';
import l10n from '../../shared/strings';
import refreshListingAssetsForChannels from './helpers/refreshListingAssetsForChannels';
import refreshListingVariantAssetsForChannel from './helpers/refreshListingVariantAssetsForChannel';
import syncTreeSelectionAfterPublish from './helpers/syncTreeSelectionAfterPublish';
import { fetchVariantAssets } from './fetchVariantAssets';
import { setActiveTabByProductChannel, setSelectedChannelByProduct } from './helpers/uiAdapter';

export const publishSelectedVariantDrafts = createAsyncThunk(
  'products/publishSelectedVariantDrafts',
  async ({
    productId, channelId, listingVariantIds,
  }, { dispatch, getState, extra: { sdk, Toast } }) => {
    const listingProduct = findListingEntity(getState().products, { masterProductId: productId, channelId });

    if (!listingProduct?._id) {
      throw new Error('Staging product not found');
    }

    if (!listingVariantIds?.length) {
      throw new Error('Select at least one variant to publish');
    }

    try {
      const { data } = await sdk.products.publishListing(
        [listingProduct._id],
        { variantIds: listingVariantIds },
      );

      if (data.failed?.length) {
        handlePublishListingFailures(data.failed, { Toast, showPublishFailedDialog });
        const publishError = new Error(data.failed[0]?.error?.message || 'Publishing failed');
        publishError.publishFailuresHandled = true;
        throw publishError;
      }

      const updatedListing = data.published[0];

      await dispatch(getListingVariants({
        masterProductId: productId,
        channelId,
        listingProductId: updatedListing._id,
      })).unwrap();

      syncTreeSelectionAfterPublish({
        dispatch,
        getState,
        productId,
        channelId,
        listing: updatedListing,
      });

      const { user: { team } } = getState();

      showPublishedToChannelToast({
        Toast,
        channelId,
        salesChannels: team?.salesChannels || [],
      });

      handlePublishVariantMediaWarnings(data.published, { showPublishFailedDialog });

      await refreshListingAssetsForChannels({
        dispatch,
        getState,
        productId,
        listings: [{ channelId, listingProductId: updatedListing._id }],
      });

      await refreshListingVariantAssetsForChannel({
        dispatch,
        fetchVariantAssets,
        productId,
        productsState: getState().products,
        channelId,
      });

      return {
        productId,
        channelId,
        updatedListing,
      };
    } catch (error) {
      if (!error.publishFailuresHandled) {
        Toast(l10n.TOAST.PUBLISHING_SERVER_ERROR, {
          type: 'error',
          autoClose: false,
          btnOkValue: l10n.TOAST.PUBLISHING_TRY_AGAIN,
          onOk: () => dispatch(publishSelectedVariantDrafts({ productId, channelId, listingVariantIds })),
        });
      }
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(publishSelectedVariantDrafts.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(publishSelectedVariantDrafts.fulfilled, (state, { payload }) => {
      const { productId, channelId, updatedListing } = payload;
      state.isBusy = false;

      if (!updatedListing) return;

      const existingListing = state.entities?.listings?.[updatedListing._id];

      setActiveTabByProductChannel(state, productId, channelId, updatedListing.listingStatus);
      upsertListing(state, {
        ...existingListing,
        ...updatedListing,
        masterProductId: productId,
      });
      setSelectedChannelByProduct(state, productId, channelId);
    })
    .addCase(publishSelectedVariantDrafts.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot publish selected variants';
    });
};
